import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuthStore } from "@/features/auth/store/authStore";
import { TAX_FILINGS_TAB_VALUES } from "@/features/tax-filings";
import { CompleteRegistrationPage } from "@/pages/CompleteRegistrationPage";
import { EmailVerificationPage } from "@/pages/EmailVerificationPage";
import { FilesPage } from "@/pages/FilesPage";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import LoginPage from "@/pages/LoginPage";
import MyFormsPage from "@/pages/MyFormsPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { ProfilePage } from "@/pages/ProfilePage";
import RegisterPage from "@/pages/RegisterPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { TaxFilingsPage } from "@/pages/TaxFilingsPage";
import TaxFilingWizardPage from "@/pages/TaxFilingWizardPage";
import { UnauthorizedPage } from "@/pages/UnauthorizedPage";
import UserDashboard from "@/pages/UserDashboard";
import { VerifyEmailSentPage } from "@/pages/VerifyEmailSentPage";
import { ProtectedRoute } from "@/routes/ProtectedRoute";
import { RoleBasedRedirect } from "@/routes/RoleBasedRedirect";
import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from "react-router-dom";

function TaxFilingsIndexRedirect() {
    const [searchParams] = useSearchParams();
    const legacyTab = searchParams.get("tab");
    if (legacyTab && (TAX_FILINGS_TAB_VALUES as string[]).includes(legacyTab)) {
        return <Navigate to={`/tax-filings/${legacyTab}`} replace />;
    }
    return <Navigate to="/tax-filings/tax-summary" replace />;
}

function App() {
    const { initAuth } = useAuthStore();

    useEffect(() => {
        initAuth();
    }, [initAuth]);

    return (
        <ErrorBoundary>
            <TooltipProvider delayDuration={300}>
                <BrowserRouter>
                    <Toaster />
                <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/register" element={<RegisterPage />} />
                    <Route
                        path="/forgot-password"
                        element={<ForgotPasswordPage />}
                    />
                    <Route
                        path="/reset-password"
                        element={<ResetPasswordPage />}
                    />
                    <Route
                        path="/verify-email"
                        element={<VerifyEmailSentPage />}
                    />
                    <Route
                        path="/email-verification"
                        element={<EmailVerificationPage />}
                    />
                    <Route
                        path="/complete-registration"
                        element={<CompleteRegistrationPage />}
                    />
                    <Route
                        path="/unauthorized"
                        element={<UnauthorizedPage />}
                    />

                    {/* Protected Routes */}
                    <Route element={<ProtectedRoute />}>
                        <Route path="/dashboard" element={<UserDashboard />} />
                        <Route path="/files" element={<FilesPage />} />
                        <Route path="/tax-filings" element={<TaxFilingsIndexRedirect />} />
                        <Route path="/tax-filings/:tab" element={<TaxFilingsPage />} />
                        <Route path="/filings" element={<Navigate to="/tax-filings/tax-summary" replace />} />
                        <Route path="/file-tax" element={<MyFormsPage />} />
                        <Route path="/file-tax/edit/:id" element={<TaxFilingWizardPage />} />
                        <Route path="/profile" element={<ProfilePage />} />
                    </Route>

                    {/* Default Routes */}
                    <Route path="/" element={<RoleBasedRedirect />} />
                    <Route path="*" element={<NotFoundPage />} />
                </Routes>
                </BrowserRouter>
            </TooltipProvider>
        </ErrorBoundary>
    );
}

export default App;
